import { useMutation, useQueryClient } from '@tanstack/react-query'
import { DEFAULT_THEME, isThemePreference, type ThemePreference } from '@bre-hub/contracts'
import { api, type Me } from './api'

// The theme (ADR 0012). The person's Hub record holds the choice; this
// browser keeps a copy so public/theme-boot.js can set it before the first
// paint. <html data-theme> is what the stylesheet reads: "light", "dark",
// or "match", which follows the device.

export const THEME_STORAGE_KEY = 'hub-theme'

export const THEME_OPTIONS: { value: ThemePreference; label: string; short: string; description: string }[] = [
  { value: 'light', label: 'Light', short: 'Light', description: 'Always light.' },
  { value: 'dark', label: 'Dark', short: 'Dark', description: 'Always dark.' },
  { value: 'match', label: 'Match my device', short: 'Device', description: 'Light or dark, following this device’s own setting.' },
]

export const themeOrDefault = (value: unknown): ThemePreference => (isThemePreference(value) ? value : DEFAULT_THEME)

function safeStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

export function storedTheme(storage: Pick<Storage, 'getItem'> | null = safeStorage()): ThemePreference {
  try {
    return themeOrDefault(storage?.getItem(THEME_STORAGE_KEY))
  } catch {
    return DEFAULT_THEME
  }
}

// Shows a theme now and remembers it for the next load in this browser.
export function applyTheme(
  value: unknown,
  root: Pick<HTMLElement, 'setAttribute'> | null = typeof document === 'undefined' ? null : document.documentElement,
  storage: Pick<Storage, 'setItem'> | null = safeStorage(),
): ThemePreference {
  const theme = themeOrDefault(value)
  root?.setAttribute('data-theme', theme)
  try {
    storage?.setItem(THEME_STORAGE_KEY, theme)
  } catch {
    // Private mode or blocked storage: the theme still shows, from the
    // Hub record, once the session loads.
  }
  return theme
}

// Changes your theme: shown at once, saved on your Hub record, and put
// back if the save fails.
export function useSetTheme(current: ThemePreference) {
  const queryClient = useQueryClient()
  const show = (theme: ThemePreference) => {
    applyTheme(theme)
    queryClient.setQueryData<Me>(['me'], (me) => (me ? { ...me, theme } : me))
  }
  return useMutation({
    mutationFn: (theme: ThemePreference) => api.setTheme(theme),
    onMutate: (theme) => {
      show(theme)
      return { previous: current }
    },
    onError: (_error, _theme, context) => show(context?.previous ?? DEFAULT_THEME),
  })
}
